import ContentCopy from '@mdi/svg/svg/content-copy.svg?react';
import TrashIcon from '@mdi/svg/svg/trash-can-outline.svg?react';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { Button, Flex, MDIIconButton, ReactTable, useModal } from '@solinkcorp/solink-components';
import {
  type ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { setFilter } from 'actions/filterActions';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { useEventSpotCheckMonthlyUsages } from 'components/SpotCheckPanel/hooks/useEventSpotCheckMonthlyUsage';
import { useMultiScheduledSpotCheckMonthlyUsages } from 'components/SpotCheckPanel/hooks/useScheduledSpotCheckMonthlyUsage';
import BouncingDots from 'loadingIndicator/BouncingDots';
import React, { useState } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import { useDispatch, useSelector } from 'stores/store';
import LocaleUtil from 'utils/LocaleUtil';
import { getRepeatOptionValue } from 'utils/RecurrenceUtil';
import { type CamerasDictionaryCamera } from 'utils/types';
import {
  REPEAT_ANNUALLY,
  REPEAT_DAILY,
  REPEAT_EVERY_15MIN,
  REPEAT_EVERY_30MIN,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
  REPEAT_MONTHLY,
  REPEAT_WEEKLY,
} from 'vd-constants';
import { type SpotCheckView } from '../../SpotCheckMainContent';
import { type SpotCheckCategoryKey } from '../SpotCheckTemplatesView';
import SpotCheckDeleteConfirmationModal from './SpotCheckDeleteConfirmationModal';
import styles from './SpotCheckTable.module.sass';

const columnHelper = createColumnHelper<any>();

interface SpotCheckTableProps {
  setView: React.Dispatch<React.SetStateAction<SpotCheckView>>;
  spotChecks: ScheduledActivityEntity[];
  eventSpotChecks: AiNotification[];
  title: string;
  selectedSpotCheckCategoryKey: SpotCheckCategoryKey | null;
  setSpotCheck: React.Dispatch<React.SetStateAction<ScheduledActivityEntity | undefined>>;
  setEventSpotCheck: React.Dispatch<React.SetStateAction<AiNotification | undefined>>;
  spotCheck: ScheduledActivityEntity | undefined;
  eventSpotCheck: AiNotification | undefined;
  setFormMode: React.Dispatch<React.SetStateAction<'create' | 'edit'>>;
  setIsExistingSpotCheck: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SpotCheckTable(props: SpotCheckTableProps) {
  const {
    setView,
    spotChecks,
    eventSpotChecks,
    title,
    selectedSpotCheckCategoryKey,
    setSpotCheck,
    setEventSpotCheck,
    spotCheck,
    eventSpotCheck,
    setFormMode,
    setIsExistingSpotCheck,
  } = props;
  const dispatch = useDispatch();
  const locale = useLocale();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const camerasById = useFluxStore(LocationStore, (store) => store.getAllLocationCamerasDict());

  const spotCheckDeleteModalProps = useModal();

  const { spotCheckMode } = useSelector((state) => ({
    spotCheckMode: state.filter.spotCheckMode,
  }));

  const isActiveMode = selectedSpotCheckCategoryKey === 'active';
  const isEventSpotCheck = spotCheckMode === 'event';

  const handleEditSpotCheck = React.useCallback(
    (spotCheck: ScheduledActivityEntity) => {
      setFormMode(isActiveMode ? 'edit' : 'create');
      setIsExistingSpotCheck(true);
      setView('spot-check-form');
      setSpotCheck(spotCheck);
      dispatch(setFilter({ selectedCameraIds: spotCheck.cameraIds, spotCheckMode: 'scheduled' }));
    },
    [dispatch, isActiveMode, setFormMode, setSpotCheck, setView, setIsExistingSpotCheck],
  );

  const handleCopySpotCheck = React.useCallback(
    (spotCheckCopy: ScheduledActivityEntity) => {
      setFormMode('create');
      setIsExistingSpotCheck(false);
      setView('spot-check-form');
      setSpotCheck(spotCheckCopy);
      dispatch(
        setFilter({ selectedCameraIds: spotCheckCopy.cameraIds, spotCheckMode: 'scheduled' }),
      );
    },
    [dispatch, setFormMode, setIsExistingSpotCheck, setView, setSpotCheck],
  );

  const handleCopyEventSpotCheck = React.useCallback(
    (eventSpotCheckCopy: AiNotification) => {
      setFormMode('create');
      setIsExistingSpotCheck(false);
      setView('spot-check-form');
      setEventSpotCheck(eventSpotCheckCopy);
      dispatch(setFilter({ spotCheckMode: 'event' }));
    },
    [dispatch, setFormMode, setIsExistingSpotCheck, setView, setEventSpotCheck],
  );

  const handleEditEventSpotCheck = React.useCallback(
    (eventSpotCheck: AiNotification) => {
      setFormMode(isActiveMode ? 'edit' : 'create');
      setIsExistingSpotCheck(true);
      setView('spot-check-form');
      setEventSpotCheck(eventSpotCheck);
      dispatch(setFilter({ spotCheckMode: 'event' }));
    },
    [dispatch, isActiveMode, setFormMode, setEventSpotCheck, setView, setIsExistingSpotCheck],
  );

  const handleDeleteSpotCheck = React.useCallback(
    (spotCheck: ScheduledActivityEntity) => {
      setSpotCheck(spotCheck);
      spotCheckDeleteModalProps.openModal();
    },
    [setSpotCheck, spotCheckDeleteModalProps],
  );
  const handleDeleteEventSpotCheck = React.useCallback(
    (eventSpotCheck: AiNotification) => {
      setEventSpotCheck(eventSpotCheck);
      spotCheckDeleteModalProps.openModal();
    },
    [setEventSpotCheck, spotCheckDeleteModalProps],
  );

  const spotChecksForMonthlyUsage = React.useMemo(
    () => (isActiveMode ? spotChecks : []),
    [isActiveMode, spotChecks],
  );

  const { monthlyUsages: scheduledSpotCheckMonthlyUsages } =
    useMultiScheduledSpotCheckMonthlyUsages(spotChecksForMonthlyUsage);
  const { monthlyUsages: eventSpotCheckMonthlyUsages, loading: eventSpotCheckLoading } =
    useEventSpotCheckMonthlyUsages(eventSpotChecks);

  // Define the columns conditionally
  const columns = React.useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('title', {
        header: locale.fields.title,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('locations', {
        header: locale.general.locations,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('frequency', {
        header: locale.scheduledAIChat.frequency,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('expiration', {
        header: locale.scheduledAIChat.expiration,
        cell: (info) => info.getValue(),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => (
          <Flex width="auto" alignItems="center" gap="1rem">
            {isActiveMode && (
              <MDIIconButton
                title={locale.general.delete}
                icon={TrashIcon}
                onClick={() => handleDeleteSpotCheck(info.row.original.spotCheck)}
                className={styles.deleteButton}
              />
            )}
            <Button
              isPrimary={false}
              onClick={() => handleEditSpotCheck(info.row.original.spotCheck)}
              className={styles.editButton}
              text={isActiveMode ? locale.general.edit : locale.general.view}
            />
            {isActiveMode && (
              <MDIIconButton
                title={locale.general.copy}
                icon={ContentCopy}
                onClick={() => {
                  const TEN_MINUTES_MS = 10 * 60 * 1000;
                  const duplicatedSpotCheck = {
                    ...info.row.original.spotCheck,
                    title: info.row.original.spotCheck.title + ' - COPY',
                    startsAt: Date.now() + TEN_MINUTES_MS,
                  };
                  handleCopySpotCheck(duplicatedSpotCheck);
                }}
                className={styles.deleteButton}
              />
            )}
          </Flex>
        ),
      }),
    ];

    if (isActiveMode) {
      baseColumns.splice(
        3,
        0,
        columnHelper.accessor('monthlyUsage', {
          header: locale.scheduledAIChat.monthlyUsage,
          cell: (info) => info.getValue(),
        }),
      );
    }

    return baseColumns;
  }, [isActiveMode, handleDeleteSpotCheck, handleEditSpotCheck, locale, handleCopySpotCheck]);

  const columnsEventSpotCheck = React.useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('title', {
        header: locale.fields.title,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('estimatedMonthlyUsage', {
        header: locale.scheduledAIChat.monthlyUsage,
        cell: (info) =>
          info.getValue() ||
          (eventSpotCheckLoading && (
            <BouncingDots key={info.getValue()} className={styles.loadingEstimatedUsage} />
          )),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => (
          <Flex width="auto" alignItems="center" gap="1rem">
            {isActiveMode && (
              <MDIIconButton
                title={locale.general.delete}
                icon={TrashIcon}
                onClick={() => handleDeleteEventSpotCheck(info.row.original.eventSpotCheck)}
                className={styles.deleteButton}
              />
            )}
            <Button
              isPrimary={false}
              onClick={() => handleEditEventSpotCheck(info.row.original.eventSpotCheck)}
              className={styles.editButton}
              text={isActiveMode ? locale.general.edit : locale.general.view}
            />
            {isActiveMode && (
              <MDIIconButton
                title={locale.general.copy}
                icon={ContentCopy}
                onClick={() => {
                  const original = info.row.original.eventSpotCheck;
                  const duplicatedEventSpotCheck: AiNotification = {
                    ...original,
                    reportTemplateId: '',
                    metadata: {
                      ...original.metadata,
                      title: (original.metadata?.title ?? '') + ' - COPY',
                    },
                  };
                  handleCopyEventSpotCheck(duplicatedEventSpotCheck);
                }}
                className={styles.deleteButton}
              />
            )}
          </Flex>
        ),
      }),
    ];

    return baseColumns;
  }, [
    isActiveMode,
    handleDeleteEventSpotCheck,
    handleEditEventSpotCheck,
    locale,
    eventSpotCheckLoading,
    handleCopyEventSpotCheck,
  ]);

  const data = React.useMemo(
    () =>
      spotChecks.map((spotCheck) => ({
        spotCheck,
        title: spotCheck.title,
        locations: getLocationCount(spotCheck, camerasById),
        frequency: getFrequencyText(
          getRepeatOptionValue(spotCheck.rule, spotCheck.reoccurringCount) as RepeatOptionValue,
          locale,
        ),
        expiration: getExpirationText(spotCheck.endsAt, spotCheck.reoccurringCount, locale),
        monthlyUsage: isActiveMode
          ? (scheduledSpotCheckMonthlyUsages[spotCheck.id] ?? locale.scheduledAIChat.calculating)
          : undefined,
      })),
    [spotChecks, scheduledSpotCheckMonthlyUsages, isActiveMode, camerasById, locale],
  );

  const dataEventSpotCheck = React.useMemo(
    () =>
      eventSpotChecks.map((eventSpotCheck) => ({
        eventSpotCheck,
        title: eventSpotCheck?.metadata?.title ?? locale.scheduledAIChat.eventSpotCheck,
        estimatedMonthlyUsage: eventSpotCheckMonthlyUsages?.[eventSpotCheck.id ?? ''] ?? null,
      })),
    [eventSpotChecks, eventSpotCheckMonthlyUsages, locale],
  );

  const table = useReactTable({
    data: isEventSpotCheck ? dataEventSpotCheck : data,
    columns: isEventSpotCheck ? columnsEventSpotCheck : columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      pagination,
      columnFilters,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
  });

  return (
    <div className={styles.spotCheckTableWrapper}>
      <h2>{title}</h2>
      <ReactTable table={table} />
      {(spotCheck || eventSpotCheck) && (
        <SpotCheckDeleteConfirmationModal
          {...spotCheckDeleteModalProps}
          spotCheck={spotCheck}
          eventSpotCheck={eventSpotCheck}
        />
      )}
    </div>
  );
}

function getLocationCount(
  spotCheck: ScheduledActivityEntity,
  camerasById: Record<string, CamerasDictionaryCamera>,
) {
  const locationIds = Array.from(
    new Set(spotCheck.cameraIds.map((cameraId: string) => camerasById[cameraId]?.location?.id)),
  );

  return locationIds.length;
}

function getExpirationText(endsAt: number, reoccurringCount: number, locale: LocaleDefinition) {
  if (reoccurringCount === 0) {
    return locale.general.deleted;
  } else if (reoccurringCount === -1 && endsAt === -1) {
    return locale.usersPage.never;
  } else if (endsAt > 0) {
    return new Date(endsAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.numberOfOccurrences,
    replacements: { count: reoccurringCount },
  });
}

type RepeatOptionValue =
  | typeof REPEAT_EVERY_15MIN
  | typeof REPEAT_EVERY_30MIN
  | typeof REPEAT_HOURLY
  | typeof REPEAT_EVERY_4H
  | typeof REPEAT_EVERY_6H
  | typeof REPEAT_DAILY
  | typeof REPEAT_WEEKLY
  | typeof REPEAT_MONTHLY
  | typeof REPEAT_ANNUALLY;
function getFrequencyText(repeatOptionValue: RepeatOptionValue, locale: LocaleDefinition) {
  const mapOfFrequencyText = {
    [REPEAT_EVERY_15MIN]: locale.scheduledAIChat.every15Minutes,
    [REPEAT_EVERY_30MIN]: locale.scheduledAIChat.every30Minutes,
    [REPEAT_HOURLY]: locale.scheduledAIChat.hourly,
    [REPEAT_EVERY_4H]: locale.scheduledAIChat.every4Hours,
    [REPEAT_EVERY_6H]: locale.scheduledAIChat.every6Hours,
    [REPEAT_DAILY]: locale.scheduledAIChat.daily,
    [REPEAT_WEEKLY]: locale.scheduledAIChat.weekly,
    [REPEAT_MONTHLY]: locale.scheduledAIChat.monthly,
    [REPEAT_ANNUALLY]: locale.scheduledAIChat.annually,
  };

  return mapOfFrequencyText[repeatOptionValue] ?? 'One-time';
}
